import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { RolePermissionService } from './rolePermission.service';
import { RolePermission } from '../models/rolePermission.model';
import { PermissionFlags } from '../enums/permissionFlags.enum';

@Injectable({
  providedIn: 'root',
})
export class PermissionService {
  private permissionsMap = new Map<string, RolePermission>();
  private loaded$ = new BehaviorSubject<boolean>(false);

  constructor(
    private authService: AuthService,
    private rolePermissionService: RolePermissionService
  ) {}

  loadPermissions(): Observable<RolePermission[]> {
    if (!this.authService.isLoggedIn()) {
      this.permissionsMap.clear();
      this.loaded$.next(true);
      return of([]);
    }

    return this.rolePermissionService.getMyRolePermissions().pipe(
      tap((permissions) => {
        this.permissionsMap.clear();
        permissions.forEach((p) => this.permissionsMap.set(p.objectId, p));
        this.loaded$.next(true);
      })
    );
  }

  get isLoaded$(): Observable<boolean> {
    return this.loaded$.asObservable();
  }

  get isLoaded(): boolean {
    return this.loaded$.value;
  }

  // Read checks stay fail-open: an unregistered objectId returns true. These gate navigation and
  // visibility (route guards, sidenav, dashboard widgets), where fail-closed would hide newly-added
  // catalogue entities before roles are seeded and risk locking users out of pages that work today.
  // The backend enforces read authorization independently, so this is defence-in-depth only.
  // Capability gating goes through hasPermissionFlag, which is fail-closed on an unknown objectId.
  hasReadPermission(objectId: string): boolean {
    if (!objectId) return true;
    const permission = this.permissionsMap.get(objectId);
    if (!permission) return true;
    return !!(permission.permissions & (PermissionFlags.ReadMy | PermissionFlags.ReadHierarchy));
  }

  hasAllReadPermissions(permission: string | string[]): boolean {
    if (Array.isArray(permission)) {
      return permission.every(p => this.hasReadPermission(p));
    }
    return this.hasReadPermission(permission);
  }

  hasAnyReadPermission(objectIds: string[]): boolean {
    if (!objectIds?.length) return true;
    return objectIds.some(id => this.hasReadPermission(id));
  }

  // Capability gating is fail-closed: an objectId with no registered row (unknown entity, or the map
  // not yet loaded) returns false, so a privileged control (edit/create/delete/PII) is only enabled
  // when the role holds an explicit grant.
  //
  // This relies on GetMy (RolePermissionController) returning a sparse set — only the rows that exist
  // for the user's role, with no synthesized 0-bitmask rows. Confirmed against the backend
  // (GetCurrentUserRolePermissionsAsync builds the map straight from the persisted RolePermission rows
  // for the role: IgnorePermissionFilter + Where(RoleId ==) + ToDictionaryAsync — complete, unpaged, no
  // tenant sub-filter), so an absent objectId genuinely means "this role was never granted", not a
  // dropped/partial row. The backend consumes the same dictionary and is itself fail-closed:
  // PermissionCheckInterceptor throws AptForbiddenException when the objectId is absent. Fail-open here
  // previously enabled controls the backend would reject with 403; fail-closed aligns the two.
  //
  // Trade-off (was previously fail-open): this drops the old new-entity lockout protection. An object
  // must now be seeded into the permission set before any role can act on it. Ship a new id in
  // RolePermissionObjects and deploy before its seed migration runs and every role — including Admin —
  // is denied the capability, with no UI signal (the control simply isn't rendered). Seed migrations
  // ship with the entity in the same deploy, so this is a deploy-ordering concern, not a steady state.
  //
  // A falsy objectId keeps the bypass (true): the caller has opted out of object-level gating rather
  // than asking about an unknown object.
  hasPermissionFlag(objectId: string, flag: PermissionFlags): boolean {
    if (!objectId) return true;
    const permission = this.permissionsMap.get(objectId);
    if (!permission) return false;
    return !!(permission.permissions & flag);
  }

  // Explicitly returns undefined for falsy objectIds (unlike permission check methods which use fail-open).
  getRolePermission(objectId: string): RolePermission | undefined {
    if (!objectId) return undefined;
    return this.permissionsMap.get(objectId);
  }
}
